import React, { useState } from "react";
import { Check, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";
import DeclineReasonBox from "@/components/DeclineReasonBox";

// One-click Approve / Decline on the dashboard request banner; permanent deletions confirm first.
export default function RequestAlertActions({ n, accent, onDone }) {
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [declining, setDeclining] = useState(false);
  const a = n.action;
  const act = async (action, reason) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/admin/request-notifications/${n.id}/${action}`, action === "decline" ? { reason } : undefined);
      toast.success(data.message);
    } catch (e) {
      if (e.response?.status === 409) toast.info(e.response.data.detail);
      else { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); return; }
    }
    setBusy(""); setConfirming(false); setDeclining(false); onDone();
  };
  return (
    <>
      <Button className={`rounded-full ${a.confirm ? "bg-red-600 hover:bg-red-700" : accent}`} disabled={!!busy}
        onClick={() => (a.confirm ? setConfirming(true) : act("approve"))} data-testid="request-alert-approve-btn">
        {busy === "approve" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : a.confirm ? <Trash2 className="mr-1.5 h-4 w-4" /> : <Check className="mr-1.5 h-4 w-4" />}
        {a.approve_label}
      </Button>
      <Button variant="outline" className={`rounded-full border-slate-300 bg-white hover:bg-red-50 hover:text-red-700 ${declining ? "border-red-300 bg-red-50 text-red-700" : ""}`} disabled={!!busy}
        onClick={() => setDeclining((d) => !d)} data-testid="request-alert-decline-btn">
        <X className="mr-1.5 h-4 w-4" /> Decline
      </Button>
      {declining && <DeclineReasonBox busy={busy === "decline"} onSend={(reason) => act("decline", reason)} onCancel={() => setDeclining(false)} testId="request-alert-decline" />}
      <AlertDialog open={confirming} onOpenChange={(o) => !busy && setConfirming(o)}>
        <AlertDialogContent data-testid="request-alert-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Permanently delete this account?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p className="rounded-lg bg-red-50 px-3 py-2 font-semibold text-red-800" data-testid="request-alert-confirm-target">
                  {a.target_name || "This account"}{a.target_email ? ` · ${a.target_email}` : ""}
                </p>
                <p>This deletes the account and every record tied to it. It can't be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" disabled={!!busy} data-testid="request-alert-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-red-600 hover:bg-red-700" disabled={!!busy}
              onClick={(e) => { e.preventDefault(); act("approve"); }} data-testid="request-alert-confirm-delete">
              {busy === "approve" && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
